import React, { useMemo, useState } from 'react';
import { Button, Form } from 'react-bootstrap';

import { ErrorDiv } from './ErrorDiv';

/**
 * Payment choice for booking a session after the fact: the user's own tokens,
 * a comp (staff grant a token of a type the service accepts and it is used),
 * or no charge at all. Calls handleLateBooking({ paymentMode, variantId, reason }).
 */
export const LateBookingPayment = ({
    activeService,
    matchedTokens,
    slots,
    handleLateBooking,
    errors,
})=>{

    const needed = slots?.length || 0;
    const enoughTokens = useMemo(
        () => (matchedTokens || []).filter(token => token.number_of_tokens >= needed),
        [matchedTokens, needed]
    );
    const compTypes = activeService?.products || [];

    const [mode, setMode] = useState(enoughTokens.length ? "token" : "comp");
    const [tokenVariantId, setTokenVariantId] = useState(enoughTokens[0]?.variant_id || "");
    const [compVariantId, setCompVariantId] = useState(compTypes[0]?.variants?.[0]?.id || "");
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [localError, setLocalError] = useState(null);

    const submit = async ()=>{
        if (!reason.trim()) {
            setLocalError("A reason is required.");
            return;
        }
        if (mode === "comp" && !compVariantId) {
            setLocalError("Pick the token type to comp.");
            return;
        }
        setLocalError(null);
        setSubmitting(true);
        await handleLateBooking({
            paymentMode: mode,
            variantId: mode === "token" ? tokenVariantId : mode === "comp" ? compVariantId : null,
            reason: reason.trim(),
        });
        setSubmitting(false);
    }

    return (
        <div data-cy="late-booking-payment">
            <h5>Book a session that already happened</h5>
            <p>This booking needs {needed} token{needed === 1 ? "" : "s"}. No booking emails are sent.</p>

            <Form.Check
                type="radio"
                id="late-pay-token"
                name="late-payment-mode"
                label="Use their tokens"
                checked={mode === "token"}
                disabled={!enoughTokens.length}
                onChange={()=>setMode("token")}
            />
            {mode === "token" && enoughTokens.length > 0 &&
                <Form.Control
                    as="select"
                    className="ms-4 mb-2 w-auto"
                    value={tokenVariantId}
                    onChange={(e)=>setTokenVariantId(e.target.value)}
                >
                    {enoughTokens.map(token => (
                        <option key={token.variant_id} value={token.variant_id}>
                            {token.product_name} ({token.number_of_tokens} available)
                        </option>
                    ))}
                </Form.Control>
            }
            {!enoughTokens.length &&
                <p className="ms-4 text-muted small">They don't have {needed} matching token{needed === 1 ? "" : "s"} for this service.</p>
            }

            <Form.Check
                type="radio"
                id="late-pay-comp"
                name="late-payment-mode"
                label={`Comp it (grant ${needed} token${needed === 1 ? "" : "s"} and use ${needed === 1 ? "it" : "them"})`}
                checked={mode === "comp"}
                disabled={!compTypes.length}
                onChange={()=>setMode("comp")}
            />
            {mode === "comp" &&
                <Form.Control
                    as="select"
                    className="ms-4 mb-2 w-auto"
                    value={compVariantId}
                    onChange={(e)=>setCompVariantId(e.target.value)}
                >
                    {compTypes.map(product => (
                        <option key={product.id} value={product.variants?.[0]?.id}>
                            {product.name}
                        </option>
                    ))}
                </Form.Control>
            }

            <Form.Check
                type="radio"
                id="late-pay-none"
                name="late-payment-mode"
                label="No charge (no token is used)"
                checked={mode === "none"}
                onChange={()=>setMode("none")}
            />

            <Form.Group className="mt-3" controlId="late-booking-reason">
                <Form.Label>Reason (required)</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={2}
                    maxLength={500}
                    placeholder="e.g. Attended without booking, confirmed by the trainer"
                    value={reason}
                    onChange={(e)=>setReason(e.target.value)}
                />
            </Form.Group>

            <Button className="mt-3" onClick={submit} disabled={submitting} data-cy="late-booking-submit">
                {submitting ? "Booking..." : "Book this session"}
            </Button>
            <ErrorDiv errors={localError ? [localError] : errors} />
        </div>
    )
}
